import { useState, useRef, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import uuid from "react-uuid";
import { Tooltip } from "@mui/material";
import { useTheme } from "styled-components";
import { useTransition, animated } from "@react-spring/web";

import SendIcon from "@mui/icons-material/SendRounded";
import MicIcon from "@mui/icons-material/MicRounded";
import AttachmentIcon from "@mui/icons-material/AttachFileRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";

import { WebSocketContext } from "../../../../../App";
import {
  clearChat,
  logEventRecord
} from "../../../../../redux/actions/chatActions";
import {
  startAudioRecording,
  triedAudioRecording
} from "../../../../../redux/actions/mediaRecorderActions";
import { openAccessibilityMenu } from "../../../../../redux/actions/chatActions";
import {
  ACCEPTED_ATTACHMENT_TYPES,
  CHAT_ON_SLACK,
  CHAT_ON_SUNSHINE,
  CHAT_WITH_AGENT,
  CHAT_WITH_BOT,
  NO_INTERACTION,
  TICKET_CREATED
} from "../../../../../constants/chat";
import useLocalization from "../../../../../hooks/localization";
import {
  ATTACHMENT_REMOVED_EVENT,
  ATTACHMENT_SELECTED_EVENT,
  ATTACHMENT_SUB_EVENT
} from "../../../../../constants/events";
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';

const MAX_REPLY_BAR_HEIGHT = 120; // px

const Wrapper = styled.div`
  width: 100%;
`;

const ReplyContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 0.25rem;
  padding: 0.25em 0.5em;
  width: 98%;
  border: 1px solid ${({ theme }) => theme.darkGrey};
  border-radius: 1.5rem;
  font-size: 1rem;
  font-family: ${({ theme }) => theme.fontFamily};

  @media (max-height: 380px) {
    padding: 0.08rem  0.25rem;
    height: ${({ theme }) => theme.xxxl};
  }
`;

const TextAreaField = styled.textarea`
  box-sizing: border-box;
  flex-grow: 1;
  max-height: ${MAX_REPLY_BAR_HEIGHT}px;
  padding: 0.25rem 0.1rem;
  resize: none;
  font-size: inherit;
  line-height: 1.4;
  background-color: transparent;
  font-size: ${({ theme }) => theme.md};
  font-color: ${({ theme }) => theme.fontColor};
  border: none;
  outline: none;
  box-shadow: none;
  overflow: auto;
  font-family: ${({ theme }) => theme.fontFamily};
  -ms-overflow-style: none;
  transition: width 200ms ease-in;
  &::placeholder {
    color: ${({ theme }) => theme.veryDarkGrey};
  }
  &::-webkit-scrollbar {
    background: transparent;
    width: 0;
  }
  @-moz-document url-prefix() {
    scrollbar-width: none;
  }
`;

const IconContainer = styled(animated.div)`
  align-self: flex-end;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 0.25rem;
`;

const IconButton = styled.button`
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0 6px 5px;
  background-color: transparent;
  box-shadow: none;
  text-transform: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  align-self: flex-end;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0 6px 5px;
  background-color: transparent;
  box-shadow: none;
  text-transform: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
`;

const FileToUpload = styled.div`
  display: flex;
  column-gap: 0.5rem;
  align-items: center;
  padding: 0.25rem 0.125em;
  font-size: ${({ theme }) => theme.reg};
  font-color: ${({ theme }) => theme.fontColor};
  overflow: hidden;
`;

const Filename = styled.div`
  overflow: hidden;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Disclaimer = styled(animated.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  padding: 0.25rem 0.5rem;
  font-size: ${({ theme }) => theme.reg};
  color: ${({ theme }) => theme.veryDarkGrey};
  font-style: italic;
  line-height: 1.5;
  background: transparent;
  user-select: none;

  @media (max-height: 380px) {
    font-size: ${({ theme }) => theme.xs};
  }
`;

const ICON_TRANSITION = {
  initial: { translateX: "0px", opacity: 1 },
  from: { translateX: "20px", opacity: 0 },
  enter: { translateX: "0px", opacity: 1 },
  leave: { translateX: "20px", opacity: 0 }
};

function Reply({ handleUserQuery, showNotification, handleUserAttachment }) {
  const [value, setValue] = useState("");
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState("default-key");
  const orgDetails = useSelector((state) => state?.org?.details);
  const isBotTyping = useSelector((state) => state?.chat?.isBotTyping);
  const tooltips = useSelector(
    (state) => state?.org?.customizations?.tooltips?.replyBar
  );
  const lang = useSelector((state) => state?.org?.lang);
  const activeForm = useSelector((state) => state?.form?.activeForm);

  const chatStage = useSelector((state) => state?.chat?.chatStage);
  const chatWith = useSelector((state) => state?.chat?.chatWith);
  const chatMedium = useSelector((state) => state?.chat?.chatMedium);
  const currentWorkflow = useSelector((state) => state?.chat?.currentWorkflow);
  const lastSavedChatRecord = useSelector(
    (state) => state?.chat?.lastSavedChatRecord?.messages
  );
  const lastMessage = useSelector((state) => {
    const conversation = state?.chat?.conversation;
    const len = conversation?.length;
    if (len) {
      return conversation[len - 1];
    }
  });

  const customizations = useSelector((state) => state?.org?.customizations);
  const canAccessDevices = useSelector((state) => state?.recorder?.canAccessDevices);

  const allowOneTimeUpload = useSelector(
    (state) => state?.attachments?.allowOneTimeUpload
  );
  const fileValidation = useSelector((state) => state?.attachments?.validation);
  const acceptedFileTypes = Array.isArray(fileValidation?.mimeType)
    ? fileValidation.mimeType
    : ACCEPTED_ATTACHMENT_TYPES;

  const dispatch = useDispatch();

  const textareaRef = useRef(null);

  const theme = useTheme();
  const { localizeText, localizeTooltips } = useLocalization();

  const localizationConfig = customizations?.localizationConfig;
  const vcConfig = customizations?.voiceChatConfig;
  const localizedTooltips = localizeTooltips(tooltips);
  const supportedLangs = localizationConfig?.supported
    ? { ...localizationConfig?.supported }
    : null;

  const hideReplyBar = activeForm
    || lastMessage?.forceReplySelection
    || (!!currentWorkflow?.id && !currentWorkflow.runInBackground);
  const showMic = vcConfig?.enabled && canAccessDevices;
  const chattingWithAgent = chatWith === CHAT_WITH_AGENT
    && (chatMedium === CHAT_ON_SLACK || chatMedium === CHAT_ON_SUNSHINE);
  const disableMessageSubmission = (chatWith === CHAT_WITH_BOT) && isBotTyping;
  const showUpload = window.FileReader && (allowOneTimeUpload || chattingWithAgent);
  const showTranslate = typeof (lang) === "string"
    && lang.length > 0
    && localizationConfig?.default
    && supportedLangs
    && chatWith === CHAT_WITH_BOT;

  if (showTranslate && !supportedLangs?.[lang]) {
    supportedLangs[lang] = lang[0].toUpperCase() + lang.slice(1);
  }

  const showAltActions = !disableMessageSubmission && !value?.length;
  const showDisclaimer = chatStage === NO_INTERACTION
    && !!customizations?.tooltips?.chatDisclaimer;
  const iconTransition = useTransition(showAltActions, ICON_TRANSITION);
  const submitIconTransition = useTransition(
    !disableMessageSubmission,
    ICON_TRANSITION
  );
  const disclaimerTransition = useTransition(showDisclaimer, {
    initial: { translateY: "0px", opacity: 1 },
    from: { translateY: "4px", opacity: 0 },
    enter: { translateY: "0px", opacity: 1 },
    leave: { translateY: "4px", opacity: 0 }
  });

  const webSocketContext = useContext(WebSocketContext);

  const logEvent = (type, title) => {
    dispatch(
      logEventRecord("", null, uuid(), type, ATTACHMENT_SUB_EVENT, true, title)
    );
  };

  const clearChatCache = () => {
    webSocketContext?.sendJsonMessage({
      action: "resetChat",
      orgid: orgDetails?.id
    });
  };

  const handleQuery = () => {
    
    const trimmed = value.trim();
      if (!trimmed || disableMessageSubmission) {

      if (textareaRef?.current) {
        textareaRef.current.focus();
      }
      return;
    }
    const query = value.trim();
    if (query?.length > 0) {
      handleUserQuery(query.slice(0, 999));
    }
    if (file?.name) {
      handleUserAttachment(file);
    }
    setTimeout(() => { // delay this to remove newline
      setValue("");
      setFile(null);
      setFileKey(uuid());
    }, 1);
  };

  const handleReplyBarHeight = () => {
    if (!textareaRef.current) {
      return;
    }
    const textarea = textareaRef.current;
    textarea.style.height = "0";
    textarea.style.height = `${Math.min(
      textarea.scrollHeight, MAX_REPLY_BAR_HEIGHT
    )}px`;
  };

  const handleChange = (evt) => {
    if (chatStage === TICKET_CREATED && lastSavedChatRecord) {
      clearChatCache();
      dispatch(clearChat(false));
    }
    setValue(evt.target.value);
    setTimeout(() => {
      handleReplyBarHeight();
    }, 1);
  };

  const handleKeyDown = (evt) => {
    if (evt.shiftKey || evt.key !== "Enter") {
      return;
    }
    if (disableMessageSubmission && evt.key === "Enter") {
      evt.preventDefault();
      return;
    }
    handleQuery();
    setTimeout(() => {
      handleReplyBarHeight();
    }, 1);
  };

  const startRecording = () => {
    if (disableMessageSubmission) {
      return;
    }
    dispatch(triedAudioRecording());
    dispatch(startAudioRecording(handleUserQuery));
  };

  const handleFileUpload = (event) => {
    if (disableMessageSubmission) {
      return;
    }
    const files = event.target.files;
    if (!showUpload || files?.length !== 1) {
      setFileKey(uuid());
      setFile(null);
      return;
    }
    const maxSize = Number.isNaN(parseInt(`${fileValidation?.sizeLimit}`))
      ? 3_000_000
      : parseInt(`${fileValidation.sizeLimit}`);
    if (!acceptedFileTypes.some((t) => t === files[0].type)) {
      setFileKey(uuid());
      setFile(null);
      showNotification(
        `Attachment "${files[0]?.name}" is not of a supported type!`,
        "info"
      );
      return;
    }
    if (files[0].size > maxSize) {
      setFileKey(uuid());
      setFile(null);
      showNotification(
        `Attachment "${files[0]?.name}" exceeds the max size limit of 5 MB!`,
        "info"
      );
      return;
    }
    setFile(files[0]);
    logEvent(ATTACHMENT_SELECTED_EVENT, `Selected ${files[0]?.name} to upload`);
  };

  const removeFile = () => {
    setFile(null);
    setFileKey(uuid());
    logEvent(ATTACHMENT_REMOVED_EVENT, `Removed ${file?.name}`);
  };

  // For the IconButton
  const handleButtonKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      dispatch(openAccessibilityMenu());
    }
  };

  const iconStyle = { fontSize: theme.xl, color: theme.veryDarkGrey, cursor: "pointer" };

  if (hideReplyBar) {
    return null;
  }

  return (
    <Wrapper>
      {disclaimerTransition((style, showDisclaimer) => (
        showDisclaimer && (
          <Disclaimer style={style}>
            {localizeText(customizations?.tooltips?.chatDisclaimer)}
          </Disclaimer>
        )
      ))}
      {file && (
        <FileToUpload>
          <Filename>
            {file?.name} · {(file?.size / 1_000_000).toFixed(2)} MB
          </Filename>
          <Tooltip title={localizedTooltips?.removeFile} placement="top-end">
            <ClearRoundedIcon
              onClick={removeFile}
              sx={{ fontSize: "inherit", color: "inherit", cursor: "pointer" }} />
          </Tooltip>
        </FileToUpload>
      )}
      <ReplyContainer>
        <TextAreaField
          ref={textareaRef}
          rows={1}
          placeholder={localizedTooltips?.replyPlaceholder}
          value={value}
          onKeyDown={handleKeyDown}
          maxLength={1000}
          onInput={handleChange}
          aria-label= {localizedTooltips?.chatAssistVisibleLabel}
          autoFocus
        />
        {iconTransition((style, showIcons) => (
          showIcons && (
            <IconContainer style={style}>
              {showUpload && (
                <Tooltip title={localizedTooltips?.attachFile} placement="top-end">
                  <FileInputLabel htmlFor="file-upload">
                    <AttachmentIcon sx={{ ...iconStyle, transform: "rotate(30deg)" }} />
                  </FileInputLabel>
                  <FileInput
                    key={fileKey}
                    type="file"
                    id="file-upload"
                    name="file-upload"
                    onChange={handleFileUpload}
                    accept={acceptedFileTypes.join(",")} />
                </Tooltip>
              )}
              {showMic && (
                <Tooltip title={localizedTooltips?.record} placement="top-end">
                  <IconButton onClick={startRecording} aria-label={localizedTooltips?.record} id="mic-icon"> 
                    <MicIcon sx={iconStyle} />
                  </IconButton>
                </Tooltip>
              )}
              {
                <div style={{ position: "relative" , display: "flex", alignItems: "center" }}>
                  <Tooltip title={localizedTooltips?.accessibilityMenu} placement="top-end">
                    <IconButton
                      onClick={() => dispatch(openAccessibilityMenu())}
                      onKeyDown={handleButtonKeyDown}
                      aria-label={localizedTooltips?.accessibilityMenu}
                      id="accessibility-icon"
                    >
                      <AccessibilityNewIcon sx={iconStyle} />
                    </IconButton>
                  </Tooltip>
                </div>
              }
            </IconContainer>
          )
        ))}
        {submitIconTransition((style, showIcon) => (
          showIcon && (
            <IconContainer style={style}>
              <Tooltip title={localizedTooltips?.submit} placement="top-end">
                <IconButton onClick={() => handleQuery()} aria-label={localizedTooltips?.submit}>
                  <SendIcon sx={iconStyle} />
                </IconButton>
              </Tooltip>
            </IconContainer>
          )
        ))}
      </ReplyContainer>
    </Wrapper>
  );
}

export default Reply;
